'use client';

import type {ButtonProps, UseRadioProps} from '@chakra-ui/react';
import {Text, resolveStyleConfig, useId, useRadio, useTheme} from '@chakra-ui/react';
import type {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import type {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {ChakraLabel, ChakraDiv} from '@linx-security/ui/utils/chakra-elements';
import {dataAttr} from '@utils';
import type {FC} from 'react';
import {useMemo} from 'react';

import {RadioButtonIcon} from './RadioButtonIcon';
import {useStyles} from './context';
import {parts} from './theme/anatomy';
import {itemStyle} from './theme/theme';

type Props = Omit<ButtonProps, 'icon' | 'colorScheme'> & {
  value: string;
  radioProps?: UseRadioProps;
  icon?: IconDefinition;
  isChecked?: boolean;
  textProps?: React.ComponentProps<typeof Text>;
  iconProps?: React.ComponentProps<typeof FontAwesomeIcon>;
  colorScheme?:
    | {
        bgColor: string;
        color: string;
      }
    | ('danger' | 'warning' | 'success' | 'info');
};

const RadioButton: FC<Props> = props => {
  const {item, 'item-container': containerStyle} = useStyles();
  const theme = useTheme();
  const {radioProps, icon, children, isChecked, isDisabled, colorScheme, textProps, iconProps, ...rest} = props;
  const {getInputProps, getRadioProps, getLabelProps} = useRadio(radioProps);
  const id = useId(undefined, `radio-button`);
  const inputProps = getInputProps();
  const checkboxProps = getRadioProps();
  const labelProps = getLabelProps();

  // @ts-expect-error: We allow passing here colorScheme as object
  const style = useMemo(() => resolveStyleConfig(itemStyle)({colorScheme, theme}), [colorScheme, theme]);

  return (
    <ChakraLabel
      __css={containerStyle}
      {...{'data-disabled': dataAttr(isDisabled), 'data-checked': dataAttr(isChecked)}}
      {...labelProps}
    >
      <input {...inputProps} aria-labelledby={id} />
      <ChakraDiv
        __css={{...item, ...style}}
        {...parts.item.attrs}
        data-checked={dataAttr(inputProps.checked)}
        id={id}
        {...checkboxProps}
        {...rest}
      >
        <RadioButtonIcon
          icon={icon}
          colorScheme={colorScheme}
          isChecked={inputProps.checked}
          isFocused={checkboxProps[`data-focus`] === `` || checkboxProps[`data-focus-visible`] === ``}
          isActive={checkboxProps[`data-active`] === ``}
          isHovered={checkboxProps[`data-hover`] === ``}
          iconProps={iconProps}
        />
        <Text size={`sm`} color={`inherit`} variant={`secondary`} {...textProps}>
          {children}
        </Text>
      </ChakraDiv>
    </ChakraLabel>
  );
};

export default RadioButton;
export type {Props as RadioButtonProps};
